import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import { getAllOrdersOfAdmin } from "../../redux/actions/order";
import { DataGrid } from "@material-ui/data-grid";
import {
  HiOutlineShoppingBag,
  HiOutlineUsers,
  HiOutlineCube,
  HiOutlineTrendingUp,
  HiOutlineLightningBolt,
  HiOutlineChatAlt2,
  HiOutlineShieldCheck,
  HiOutlineTicket,
  HiOutlineArrowRight,
} from "react-icons/hi";
import { FiPackage, FiEye } from "react-icons/fi";
import { Button } from "@material-ui/core";
import { getOrderNumber } from "../../utils/orderUtils";

const StatCard = ({ label, value, icon: Icon, link, linkLabel, accent }) => (
  <div className={`bg-white border ${accent ? "border-l-4 border-l-accent-500 border-t-gray-100 border-r-gray-100 border-b-gray-100" : "border-gray-100"} p-5 shadow-sm`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-widest text-secondary-500 font-semibold mb-1">{label}</p>
        <p className="text-3xl font-bold text-black mt-1">{value}</p>
      </div>
      <div className={`w-11 h-11 flex items-center justify-center ${accent ? "bg-accent-500" : "bg-black"}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
    {link && (
      <Link to={link} className="inline-flex items-center gap-1 text-xs text-secondary-500 hover:text-black transition-colors mt-3 font-medium">
        {linkLabel} <HiOutlineArrowRight className="w-3 h-3" />
      </Link>
    )}
  </div>
);

const AdminDashboardMain = () => {
  const dispatch = useDispatch();
  const { adminOrders, adminOrderLoading } = useSelector((state) => state.order);
  const [analytics, setAnalytics] = useState(null);
  const [productsCount, setProductsCount] = useState(0);
  const [usersCount, setUsersCount] = useState(0);

  useEffect(() => {
    dispatch(getAllOrdersOfAdmin());
    fetchAnalytics();
    fetchCounts();
  }, [dispatch]);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${server}/order/admin/analytics`, { withCredentials: true });
      if (data.success) setAnalytics(data);
    } catch (err) { console.error("Analytics fetch error:", err); }
  };

  const fetchCounts = async () => {
    try {
      const [productsRes, usersRes] = await Promise.all([
        axios.get(`${server}/product/admin/all-products?limit=1`, { withCredentials: true }),
        axios.get(`${server}/user/admin-all-users`, { withCredentials: true }),
      ]);
      if (productsRes.data.totalCount !== undefined) setProductsCount(productsRes.data.totalCount);
      if (usersRes.data.users) setUsersCount(usersRes.data.users.length);
    } catch (err) { console.error("Counts fetch error:", err); }
  };

  const totalRevenue = analytics?.totalRevenue || adminOrders?.reduce((acc, o) => acc + (o.totalPrice || 0), 0) || 0;
  const totalOrders = adminOrders?.length || 0;

  const statusColor = (status) => {
    switch (status) {
      case "Delivered": return "bg-green-100 text-green-800";
      case "Shipped": case "Out for Delivery": return "bg-blue-100 text-blue-800";
      case "Confirmed": case "Packed": return "bg-yellow-100 text-yellow-800";
      case "Cancelled": case "Returned": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const columns = [
    {
      field: "orderNumber", headerName: "Order ID", minWidth: 180, flex: 0.8,
      headerAlign: "center", align: "center",
      renderCell: (params) => (
        <span className="text-xs font-mono text-black truncate" title={params.value}>{params.value}</span>
      ),
    },
    {
      field: "status", headerName: "Status", minWidth: 140, flex: 0.7,
      headerAlign: "center", align: "center",
      renderCell: (params) => (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColor(params.value)}`}>{params.value}</span>
      ),
    },
    {
      field: "itemsQty", headerName: "Items", type: "number", minWidth: 90, flex: 0.5,
      headerAlign: "center", align: "center",
      renderCell: (params) => (
        <div className="flex items-center gap-1"><FiPackage size={13} className="text-secondary-400" /><span className="font-medium text-secondary-700">{params.value}</span></div>
      ),
    },
    {
      field: "total", headerName: "Total", minWidth: 110, flex: 0.6,
      headerAlign: "center", align: "center",
      renderCell: (params) => (
        <span className="font-bold text-black">{params.value}</span>
      ),
    },
    {
      field: "actions", headerName: "", minWidth: 70, flex: 0.4,
      headerAlign: "center", align: "center", sortable: false,
      renderCell: (params) => (
        <Button className="!min-w-0 !p-2 !text-black hover:!bg-gray-100" onClick={() => window.open(`/admin/order/${params.id}`, "_blank")}>
          <FiEye size={16} />
        </Button>
      ),
    },
  ];

  const rows = (adminOrders || []).map((item) => ({
    id: item._id,
    orderNumber: getOrderNumber(item),
    itemsQty: item?.cart?.reduce((acc, c) => acc + c.qty, 0) || 0,
    total: `\u20B9${item?.totalPrice?.toLocaleString("en-IN")}`,
    status: item?.status,
  }));

  const quickLinks = [
    { label: "Services / Events", icon: HiOutlineTicket, to: "/admin-services", desc: "Manage wellness workshops & events" },
    { label: "Offer Management", icon: HiOutlineChatAlt2, to: "/admin-offer-management", desc: "Review buyer offers & forward to vendors" },
    { label: "Auction Management", icon: HiOutlineLightningBolt, to: "/admin-auction-management", desc: "Create & manage live auctions" },
    { label: "Verification", icon: HiOutlineShieldCheck, to: "/admin-verification", desc: "Release vendor payments after 3-way check" },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="p-4 sm:p-6 lg:p-8">

        {/* Page Header */}
        <div className="mb-8 pb-6 border-b border-secondary-200">
          <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin Dashboard</p>
          <h1 className="text-3xl font-bold text-black">Platform Overview</h1>
          <p className="text-secondary-500 text-sm mt-1">Manage your marketplace end-to-end.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Revenue" value={`\u20B9${totalRevenue.toLocaleString("en-IN")}`} icon={HiOutlineTrendingUp} accent />
          <StatCard label="Total Orders" value={totalOrders} icon={HiOutlineShoppingBag} link="/admin-orders" linkLabel="View orders" />
          <StatCard label="Products" value={productsCount} icon={HiOutlineCube} link="/admin-products" linkLabel="Manage" />
          <StatCard label="Customers" value={usersCount} icon={HiOutlineUsers} link="/admin-users" linkLabel="View all" />
        </div>

        {/* Order Status Summary */}
        {analytics?.ordersByStatus && (
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-secondary-500 font-semibold mb-3">Order Status Breakdown</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {Object.entries(analytics.ordersByStatus).map(([status, count]) => (
                <div key={status} className="bg-white border border-secondary-100 p-3 text-center shadow-sm">
                  <p className="text-xl font-bold text-black">{count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-secondary-500 mt-0.5">{status}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Access: 4 Feature Areas */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-widest text-secondary-500 font-semibold mb-3">Platform Features</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((item) => {
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} className="group bg-white border border-secondary-100 p-5 shadow-sm hover:border-black hover:shadow-md transition-all duration-200">
                  <div className="w-10 h-10 bg-black flex items-center justify-center mb-3 group-hover:bg-accent-500 transition-colors">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-bold text-black text-sm mb-1">{item.label}</p>
                  <p className="text-xs text-secondary-500">{item.desc}</p>
                  <div className="flex items-center gap-1 mt-3 text-xs text-secondary-400 group-hover:text-black transition-colors font-medium">
                    Manage <HiOutlineArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="bg-white border border-secondary-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-secondary-500 font-semibold">Recent Orders</p>
              <p className="text-lg font-bold text-black mt-0.5">{rows.length} total</p>
            </div>
            <Link to="/admin-orders" className="flex items-center gap-1.5 px-4 py-2 bg-black text-white text-xs font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors">
              View All <HiOutlineArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Mobile Cards */}
          <div className="block sm:hidden p-3">
            {adminOrderLoading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black" /></div>
            ) : rows.length === 0 ? (
              <p className="text-center py-8 text-secondary-400 text-sm">No orders yet</p>
            ) : (
              <div className="space-y-2">
                {rows.slice(0, 8).map((order) => (
                  <div key={order.id} className="border border-secondary-100 p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-mono text-black truncate max-w-[140px]">{order.orderNumber}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColor(order.status)}`}>{order.status}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-secondary-500">
                      <span>{order.itemsQty} items</span>
                      <span className="font-bold text-black">{order.total}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop DataGrid */}
          <div className="hidden sm:block" style={{ height: 460 }}>
            {adminOrderLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black" />
              </div>
            ) : (
              <DataGrid
                rows={rows}
                columns={columns}
                pageSize={8}
                rowsPerPageOptions={[8, 15, 25]}
                disableSelectionOnClick
                autoHeight={false}
                className="!border-0"
                sx={{
                  "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "1px solid #e5e5e5" },
                  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "700", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "#525252" },
                  "& .MuiDataGrid-row:hover": { backgroundColor: "#fafafa" },
                  "& .MuiDataGrid-cell": { borderBottom: "1px solid #f5f5f5" },
                  "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #e5e5e5", backgroundColor: "#f5f5f5" },
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardMain;